'use client';

import { Component, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

/**
 * Корневой Error Boundary.
 * Оборачивает дерево провайдеров и ловит необработанные ошибки рендеринга,
 * предотвращая белый экран.
 *
 * Inline-стили — намеренно: при краше Tailwind/ThemeProvider могут не работать.
 */
export class AppErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('[AppErrorBoundary]', error, info.componentStack);
    // Лог в собственный трекер с component stack — там видны РЕАЛЬНЫЕ имена
    // компонентов (Header, Catalog, ProductPopup и т.д.), которые в обычном
    // stack production-React заменены на минифицированные React-internals.
    //
    // componentStack выглядит так:
    //   at Header (src/components/layout/header.tsx:142:18)
    //   at HomePage (src/app/page.tsx:82:5)
    //   at Providers (src/components/providers/index.tsx:48:9)
    // — это золото для отладки #418.
    import('@/lib/source-tracker')
      .then(({ logClientError }) => {
        const enrichedError = new Error(error.message);
        enrichedError.name = error.name;
        // Объединяем JS stack + React component stack для полной картины
        enrichedError.stack =
          (error.stack || '') +
          (info.componentStack ? '\n\nComponent stack:' + info.componentStack : '');
        logClientError(`React render error: ${error.message}`, enrichedError);
      })
      .catch(() => { /* swallow */ });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '2rem',
            fontFamily: 'Montserrat, system-ui, -apple-system, sans-serif',
            backgroundColor: '#F8F8F6',
            color: '#2C1360',
          }}
        >
          <div style={{ textAlign: 'center', maxWidth: '400px' }}>
            <h1 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '0.75rem' }}>
              Что-то пошло не так
            </h1>
            <p style={{ fontSize: '0.875rem', color: '#6B7280', marginBottom: '1.5rem', lineHeight: 1.5 }}>
              Произошла ошибка при загрузке. Попробуйте обновить страницу.
            </p>
            <button
              onClick={() => window.location.reload()}
              style={{
                padding: '0.75rem 1.5rem',
                borderRadius: '0.75rem',
                border: 'none',
                backgroundColor: '#7B42FF',
                color: '#FFFFFF',
                fontWeight: 600,
                fontSize: '0.875rem',
                cursor: 'pointer',
              }}
            >
              Обновить страницу
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
